import * as Sentry from '@sentry/react';

// Observabilidad de fallas alrededor del flujo de cook time / ETA.
//
// Generaliza el patrón de `utils/print/printSentry.ts` (setContext + fingerprint + tags)
// para los dos puntos donde una falla de cook time deja al comercio sin feedback útil:
//  - aceptar un pedido con ETA (handleUpdateStatus → PUT /v1/pedido con cook_time)
//  - guardar el tiempo de preparación por defecto (handleSubmitChanges con default_cook_time)
//
// Síncrono y tolerante: si Sentry no está inicializado (no-prod), no rompe la app.

type CaptureOrderErrorOptions = {
  // tag `order_error` — agrupa por causa en Sentry (fingerprint), no por stack.
  tag: 'order_status_update_failed' | 'order_accept_with_eta_failed';
  order_id?: string;
  cook_time?: number;
  http_status?: number;
};

const toError = (error: unknown): Error => (error instanceof Error ? error : new Error(String(error)));

export const captureOrderError = (error: unknown, options: CaptureOrderErrorOptions): void => {
  try {
    Sentry.withScope((scope) => {
      scope.setTag('order_error', options.tag);
      if (options.http_status !== undefined) scope.setTag('http_status', String(options.http_status));
      scope.setFingerprint(['order', options.tag, String(options.http_status ?? 'unknown')]);
      scope.setContext('order', {
        tag: options.tag,
        order_id: options.order_id ?? null,
        cook_time: options.cook_time ?? null,
        http_status: options.http_status ?? null,
      });
      Sentry.captureException(toError(error));
    });
  } catch {
    // off — la observabilidad no debe romper la app
  }
};

type CaptureCommerceSaveErrorOptions = {
  // tag `commerce_save_failed` reservado para guardado de settings del comercio.
  dirtyKeys?: string[];
  http_status?: number;
};

// Falla al guardar settings del comercio (incluye `default_cook_time`). `dirtyKeys`
// permite saber QUÉ campos intentaba guardar el comercio cuando falló.
export const captureCommerceSaveError = (error: unknown, options: CaptureCommerceSaveErrorOptions = {}): void => {
  try {
    Sentry.withScope((scope) => {
      scope.setTag('order_error', 'commerce_save_failed');
      if (options.http_status !== undefined) scope.setTag('http_status', String(options.http_status));
      scope.setFingerprint(['commerce', 'commerce_save_failed']);
      scope.setContext('commerce_save', {
        dirty_keys: options.dirtyKeys ?? [],
        http_status: options.http_status ?? null,
      });
      Sentry.captureException(toError(error));
    });
  } catch {
    // off — la observabilidad no debe romper la app
  }
};
